import type { ColumnDef, SortingState } from '@tanstack/react-table';
import { getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import type { ForwardedRef, ReactElement, ReactNode, Ref } from 'react';
import { forwardRef, useImperativeHandle, useState } from 'react';
import { TableBase } from '../TableBase/TableBase';
import { TableBaseProps } from '../TableBase/types';
import type { FrontTableAPI, TableElement } from '../types';

export type FrontTableViewProps<T extends TableElement> = {
  tableData: T[];
  columns: ColumnDef<T>[];
  initialSorting?: SortingState;
  className?: string;
  pager?: ReactNode;
  notFound?: string;
} & Pick<TableBaseProps<T>, 'innerWrapperClassName' | 'isLoading' | 'onRowClick' | 'caption' | 'footer' | 'notFound'>;

const FrontTableComp = <T extends TableElement>(
  { initialSorting = [], columns, tableData, isLoading = false, pager, ...rest }: FrontTableViewProps<T>,
  ref: Ref<FrontTableAPI<T>>
) => {
  const [sorting, setSorting] = useState<SortingState>(initialSorting);

  const table = useReactTable({
    columns: columns,
    data: tableData || [],
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    sortDescFirst: false,
    state: {
      sorting,
    },
    defaultColumn: {
      minSize: 50,
      size: 600,
      sortingFn: 'text',
    },
    getRowId: (row) => row.id!,
  });

  useImperativeHandle(ref, () => ({
    tableApi: table,
  }));

  return <TableBase<T> table={table} isLoading={isLoading} pagination={pager} {...rest} />;
};

export const FrontTable = forwardRef(FrontTableComp) as <T extends { id?: string }>(
  props: FrontTableViewProps<T> & { ref?: ForwardedRef<FrontTableAPI<T>> }
) => ReactElement;
